import { useLoaderData } from '@remix-run/react';
import type { loader as homeLoader } from '~/routes/_app._index';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { RoccoLink } from '@rocco/components/link';
import { Image } from '@rocco/components/image';
import { DiscoverHeader } from '../components/DiscoverHeader';
import { CarouselPaginationPill } from '../components/CarouselPaginationPill';
import { ScrollReveal } from '../components/ScrollReveal';

/**
 * Discover Professionals (Figma `997:33934` + `997:34729`, white section).
 *
 * The light counterpart of Discover Projects: shared editorial header, a
 * carousel of live featured-professional portrait cards (`professionalSection
 * .featured`), then a "Get Matched" prompt + outline button.
 */
const ProfessionalsPagination = () => {
    const {
        api,
        currentSlide,
        totalSlides,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
    } = useCarousel();

    // Hide pagination when every slide already fits (nothing to scroll).
    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center max-md:pt-8 md:pt-[2.5vw]">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Professionals carousel"
            />
        </div>
    );
};

interface DiscoverProfessionalsSectionProps {
    /** Override the eyebrow (e.g. logged-in "Recently viewed"). */
    eyebrow?: string;
    /** Override the display heading (e.g. logged-in "Recently viewed Professionals"). */
    heading?: string;
}

export const DiscoverProfessionalsSection = ({
    eyebrow = '(02 – Professionals)',
    heading = 'Connect with Professionals',
}: DiscoverProfessionalsSectionProps = {}) => {
    const { professionalSection, homeCopy, professionalPopularCategories } =
        useLoaderData<typeof homeLoader>();
    // The loader merges pinned + trending and applies the size limit; every
    // pinned professional renders even when there are more than nine.
    const featured = professionalSection?.featured ?? [];

    return (
        <section className="w-full overflow-hidden bg-white text-[#181818]">
            <DiscoverHeader
                theme="light"
                fullWidthRule
                eyebrow={eyebrow}
                heading={heading}
                headingClassName="md:tracking-[-0.4375vw]"
                categories={professionalPopularCategories}
                description={homeCopy.professionalsDescription}
                ctaLabel="View all Professionals"
                ctaTo="/professionals"
            />

            {/* Professional cards — full-bleed, centre-aligned so partial
                tiles peek at both edges and the loop reads as endless */}
            <ScrollReveal delay={120} className="max-md:pt-8 md:pt-[8.33vw]">
                <Carousel
                    options={{
                        align: 'center',
                        containScroll: 'trimSnaps',
                        dragFree: true,
                        // Wrap around at either end. Embla falls back to
                        // non-loop automatically when slides don't fill the view.
                        loop: true,
                        // Mobile tiles are narrow, so per-tile snap points make
                        // the pagination dot race during a flick. Group snaps
                        // per viewport page on mobile; desktop keeps per-tile.
                        slidesToScroll: 'auto',
                        breakpoints: {
                            '(min-width: 768px)': { slidesToScroll: 1 },
                        },
                    }}
                >
                    {/* Loop-safe spacing: symmetric per-slide padding (flex gap
                        breaks at the loop seam; centre align needs symmetry). */}
                    <CarouselContent>
                        {featured.map((pro) => (
                            <CarouselItem
                                key={pro.id}
                                className="max-md:basis-[calc(38%+2.08vw)] max-md:px-[1.04vw] md:basis-[23.903vw] md:px-[0.287vw]"
                            >
                                <RoccoLink
                                    to={pro.link}
                                    className="block w-full"
                                >
                                    <div className="bg-neutral-200 relative w-full overflow-hidden max-md:aspect-[291/400] md:aspect-[446/550]">
                                        <img
                                            src={pro.tile?.images?.[0]}
                                            alt={pro.title}
                                            className="absolute inset-0 size-full max-w-none object-cover"
                                            loading="lazy"
                                        />
                                        {pro.tile?.logo?.image && (
                                            <span
                                                className="absolute left-[6.9%] top-[5.65%] flex items-center justify-center overflow-hidden rounded-full p-1 max-md:size-[10.6vw] md:size-[82px]"
                                                style={{
                                                    backgroundColor:
                                                        pro.tile.logo.variables
                                                            ?.avatarImageBackgroundColor ||
                                                        'white',
                                                }}
                                            >
                                                <Image
                                                    src={pro.tile.logo.image}
                                                    alt=""
                                                    size="professionalLogo"
                                                />
                                            </span>
                                        )}
                                    </div>
                                    <p className="m-0 font-sans leading-[1.5] text-[#1f1f21] max-md:pt-3 max-md:text-[2.6vw] md:pt-[0.6vw] md:text-[1.04vw]">
                                        {pro.title}
                                    </p>
                                    {pro.tile?.category && (
                                        <p className="m-0 font-sans leading-[1.5] text-[#707070] max-md:text-[2.6vw] md:text-[1.04vw]">
                                            {pro.tile.category}
                                        </p>
                                    )}
                                </RoccoLink>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    <ProfessionalsPagination />
                </Carousel>
            </ScrollReveal>

            {/* Get matched prompt — temporarily hidden until the "Get Matched"
                feature is merged. Restore the CTA text + button together. */}
            {/*
            <ScrollReveal className="flex flex-col items-start max-md:gap-5 max-md:px-6 max-md:pb-8 max-md:pt-6 md:gap-[1.93vw] md:px-[3.125vw] md:pb-[1.5vw] md:pt-[4vw]">
                <p className="m-0 font-sans font-medium leading-[1.3] text-[#181818] max-md:text-[3.13vw] md:w-[30.57vw] md:text-[1.302vw] md:tracking-[-0.039vw]">
                    Don’t know where to start?
                    <br />
                    Answer a few questions and get matched with trusted industry
                    professionals.
                </p>
                <RoccoLink
                    to="/onboarding"
                    className="inline-flex w-fit items-center justify-center whitespace-nowrap rounded-full border border-black bg-transparent font-sans font-medium text-black transition-colors hover:bg-black hover:text-white max-md:px-7 max-md:py-3 max-md:text-base md:h-[3.125vw] md:px-[1.436vw] md:text-[1.182vw]"
                >
                    Get Matched
                </RoccoLink>
            </ScrollReveal>
            */}
        </section>
    );
};
